import { Card, CardContent, CardHeader, Grid, makeStyles, MenuItem, Select, Typography } from "@material-ui/core";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useReBookCandidateExamMutation } from "../../../api/rtk/ExamSessionCandidateApi";
import { useGetTimeSlotStructureQuery } from "../../../api/rtk/TimeSlotStructureApi";
import { Language, SwissGermanLocale, SwissTimeZone } from "../../../data/constants";
import { ExamType, ExamTypeTranslationKey } from "../../../model/Exam";
import {
  ExamSessionCandidateExamState,
  ExamSessionCandidateExamStateTranslationKey,
  ExamSessionCandidateExamViewModel,
} from "../../../model/examSessionCandidate/ExamSessionCandidateExamViewModel";
import { ExamSessionCandidateViewModel } from "../../../model/examSessionCandidate/ExamSessionCandidateViewModel";
import { ReBookCandidateExamRequestModel } from "../../../model/examSessionCandidate/ReBookCandidateExamRequestModel";
import { ExamSessionViewModel } from "../../../model/ExamSessionViewModel";
import { GetTranslatedValueIntl } from "../../../model/Shared/TranslatedValue";
import { getExamTimeSlotLabelByIndex } from "../../../utils/TimeSlotUtils";
import FormGridCell from "../../common/FormGridCell";
import TranslatedMessage from "../../translations/TranslatedMessage";
import { getManualStates } from "./ExamSessionCandidateConstants";
import ExamSessionCandidateExamActions from "./ExamSessionCandidateExamActions";
import ExamSessionCandidateExamBookDialog from "./ExamSessionCandidateExamBookDialog";
import ExamSessionCandidateExamGrade from "./ExamSessionCandidateExamGrade";
import ExamSessionCandidateExamRow from "./ExamSessionCandidateExamRow";
import ExamSessionCandidateExamScore from "./ExamSessionCandidateExamScore";
import { CandidateModuleState } from "../../../model/examSessionCandidate/CandidateModuleState";

const useStyles = makeStyles(() => ({
  conainer: {
    width: "100%",
  },
  labelContainer: {
    display: "flex",
    alignItems: "center",
  },
  editExamStateButtonContainer: {
    marginLeft: 8,
  },
  stateSelect: {
    minWidth: 150,
  },
  stateLabel: {
    display: "inline",
  },
}));

export interface ExamSessionCandidateExamCallbacks {
  toggleExamManualState: (moduleId: string, examId: string) => void;
  manualExamStateChange: (moduleId: string, examId: string, state: ExamSessionCandidateExamState) => void;
  examScoreChange: (moduleId: string, examId: string, score?: number | null) => void;
  examGradeChange: (moduleId: string, examId: string, grade?: number | null) => void;
}

interface ExamSessionCandidateExamProps extends ExamSessionCandidateExamCallbacks {
  examSession: ExamSessionViewModel;
  examSessionCandidate: ExamSessionCandidateViewModel;
  candidateExam: ExamSessionCandidateExamViewModel;
}

const ExamSessionCandidateExam: React.FC<ExamSessionCandidateExamProps> = ({
  examSession,
  examSessionCandidate,
  candidateExam,
  manualExamStateChange,
  toggleExamManualState,
  examScoreChange,
  examGradeChange,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const { data: timeSlotStructure } = useGetTimeSlotStructureQuery();
  const [rebookCandidateExam, { isLoading: isReBookCandidateExamLoading }] = useReBookCandidateExamMutation();
  const [isLocationDateDialogOpen, setIsLocationDateDialogOpen] = useState(false);

  const exam = examSession.exams.find((exam) => exam.id === candidateExam.examId);
  const day = examSession.examDays.find((day) => day.id === candidateExam.examDayId);
  const roomAssignment = day?.roomAssignments.find((roomAssignment) => roomAssignment.roomId === candidateExam.roomId);
  const location = examSession.locations.find((location) => location.id === day?.locationId);

  if (exam === undefined) {
    return <></>;
  }

  const isBookedExam = candidateExam.state !== ExamSessionCandidateExamState.Cancelled;
  const isTermPaper = exam.type === ExamType.TermPaper;
  const none = "-";
  const examName = GetTranslatedValueIntl(intl, exam.name);
  const locationName = isBookedExam && location !== undefined ? location.name : none;
  const date =
    isBookedExam && day !== undefined
      ? new Date(day.date).toLocaleDateString(SwissGermanLocale, { timeZone: SwissTimeZone })
      : none;
  const room = location?.rooms.find((room) => room.id === roomAssignment?.roomId);
  const roomName = isBookedExam && room !== undefined ? room.name : none;
  const preparationRoomAssignment = day?.roomAssignments.find((roomAssignment) => roomAssignment.isPreparationRoom);
  const preparationRoom = location?.rooms.find((room) => room.id === preparationRoomAssignment?.roomId);
  const preparationRoomName = isBookedExam && preparationRoom !== undefined ? preparationRoom.name : none;
  const timeSlotLabel =
    isBookedExam && timeSlotStructure !== undefined && day !== undefined
      ? getExamTimeSlotLabelByIndex(timeSlotStructure, day.timeSlotStructureType, candidateExam.timeSlotIndex)
      : none;
  const isCancelled = candidateExam.state === ExamSessionCandidateExamState.Cancelled;
  const module = examSessionCandidate.modules.find((m) => m.moduleId === candidateExam.moduleId);
  const roomLanguages = roomAssignment?.languages.join(", ");

  const handleToggleExamManualState = () => toggleExamManualState(candidateExam.moduleId, candidateExam.examId);

  const handleRebook = async () => {
    if (isTermPaper) {
      await rebookExam({ isTermPaper: true });
    } else {
      setIsLocationDateDialogOpen(true);
    }
  };

  const handleManualStateChange = (event: React.ChangeEvent<{ value: unknown }>) =>
    manualExamStateChange(
      candidateExam.moduleId,
      candidateExam.examId,
      event.target.value as ExamSessionCandidateExamState
    );

  const handleScoreChange = (score?: number | null) =>
    examScoreChange(candidateExam.moduleId, candidateExam.examId, score);

  const handleGradeChange = (grade?: number | null) =>
    examGradeChange(candidateExam.moduleId, candidateExam.examId, grade);

  const handleAcceptRebook = async (locationId: string, examDayId: string, language: Language) =>
    await rebookExam({ locationId, examDayId, language });

  const rebookExam = async (data?: ReBookCandidateExamRequestModel) =>
    rebookCandidateExam({
      examSessionCandidateId: examSessionCandidate.id,
      moduleId: candidateExam.moduleId,
      examId: exam.id,
      data,
    });

  const { isManualState } = candidateExam;
  const stateRow = (
    <ExamSessionCandidateExamRow
      labelTranslationKey="examSessionCandidate.state"
      value={
        <>
          {!isManualState && (
            <Typography className={classes.stateLabel}>
              <TranslatedMessage id={ExamSessionCandidateExamStateTranslationKey[candidateExam.state]} />
            </Typography>
          )}
          {isManualState && (
            <Select className={classes.stateSelect} value={candidateExam.state} onChange={handleManualStateChange}>
              {getManualStates(candidateExam.type).map((manualState) => (
                <MenuItem key={manualState} value={manualState}>
                  {<TranslatedMessage id={ExamSessionCandidateExamStateTranslationKey[manualState]} />}
                </MenuItem>
              ))}
            </Select>
          )}
        </>
      }
    />
  );

  const locationRow = !isTermPaper && (
    <ExamSessionCandidateExamRow labelTranslationKey="location" value={<Typography>{locationName} ({roomLanguages})</Typography>} />
  );

  const examDateRow = !isTermPaper && (
    <ExamSessionCandidateExamRow
      labelTranslationKey="examSessionCandidate.examDate"
      value={<Typography>{date}</Typography>}
    />
  );

  const roomRow = !isTermPaper && (
    <ExamSessionCandidateExamRow
      labelTranslationKey="roomAssignment.field.room"
      value={<Typography>{roomName}</Typography>}
    />
  );

  const preparationRoomRow = exam.type === ExamType.Oral && (
    <ExamSessionCandidateExamRow
      labelTranslationKey="roomAssignment.field.isPreparationRoom"
      value={<Typography>{preparationRoomName}</Typography>}
    />
  );

  const timeSlotRow = !isTermPaper && (
    <ExamSessionCandidateExamRow
      labelTranslationKey="examSession.candidateExportDialog.timeSlot.label"
      value={<Typography>{timeSlotLabel}</Typography>}
    />
  );

  const gradeRow = (
    <ExamSessionCandidateExamRow
      labelTranslationKey="examScore.grade"
      value={
        <ExamSessionCandidateExamGrade
          disabled={candidateExam.isManualState}
          examState={candidateExam.state}
          grade={candidateExam.grade}
          gradeChange={handleGradeChange}
        />
      }
    />
  );

  const pointsRow = (
    <ExamSessionCandidateExamRow
      labelTranslationKey="examScore.points"
      value={
        <ExamSessionCandidateExamScore
          disabled={candidateExam.isManualState}
          examState={candidateExam.state}
          score={candidateExam.score}
          scoreChange={handleScoreChange}
        />
      }
    />
  );

  return (
    <Grid container spacing={1}>
      <FormGridCell size="full">
        <Card>
          <CardHeader
            title={examName}
            subheader={<TranslatedMessage id={ExamTypeTranslationKey[exam.type]} />}
            action={
              <ExamSessionCandidateExamActions
                examState={candidateExam.state}
                rebook={handleRebook}
                toggleExamManualState={handleToggleExamManualState}
                disabled={isReBookCandidateExamLoading || module?.refundStatus === true}
                hideRebookButton={
                  (module?.state ?? CandidateModuleState.Cancelled) === CandidateModuleState.Cancelled
                }
                hideEditStateButton={!isTermPaper || candidateExam.isManualState}
                hideResetStateButton={!isTermPaper || !candidateExam.isManualState}
                isTermPaper={isTermPaper}
              />
            }
          />
          <CardContent>
            <Grid container={true} spacing={1}>
              {stateRow}
              {!isCancelled && (
                <>
                  {locationRow}
                  {examDateRow}
                  {roomRow}
                  {preparationRoomRow}
                  {timeSlotRow}
                  {gradeRow}
                  {pointsRow}
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      </FormGridCell>
      {isLocationDateDialogOpen && (
        <ExamSessionCandidateExamBookDialog
          opened={isLocationDateDialogOpen}
          examSessionId={examSession.id}
          examId={exam.id}
          examType={exam.type}
          language={examSessionCandidate.language}
          locationId={location?.id}
          examDayId={day?.id}
          accept={handleAcceptRebook}
          decline={() => setIsLocationDateDialogOpen(false)}
        />
      )}
    </Grid>
  );
};

export default ExamSessionCandidateExam;
